import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useLexicalEditable } from '@lexical/react/useLexicalEditable';
import { DynamicDataIcon, ExternalDynamicDataIcon } from '@pbk-web/ui';
import { cx } from '@pbk-web/utils';
import type { LexicalCommand, NodeKey } from 'lexical';
import { $getNodeByKey, createCommand } from 'lexical';
import { X } from 'lucide-react';
import type { MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { useFeatureConfig } from '../../core/context/FeaturesContext';
import { useLexicalDynamicData } from './DynamicDataContext';

/** A chip was clicked to edit; the payload is its node key. */
export const EDIT_DYNAMIC_DATA_COMMAND: LexicalCommand<NodeKey> = createCommand('EDIT_DYNAMIC_DATA_COMMAND');

// Same classes as forms-v2's chip markup (`editorHtml.ts`), so both editors look the same
const BACKGROUND = { ok: 'bg-primary-100', error: 'bg-semantic-error', warning: 'bg-semantic-warning' } as const;
// V1 AnswerPlaceholderWord: a resolved value, not a definition
const ANSWER_COLOURS = 'bg-greyscale-5 text-black';

// The editor keeps its caret: a mouse-down on a chip shouldn't move it
const keepCaret = (event: MouseEvent) => event.preventDefault();

/**
 * The pill a `DynamicDataNode` renders, from the host's `chipFor`, plus ✕ while editable.
 * - `builder` display: white on the validation colour; `viewer`: a grey value chip (errors stay red).
 */
export function DynamicDataChip({ id, nodeKey }: { id: string; nodeKey: NodeKey }) {
  const [editor] = useLexicalComposerContext();
  const { t } = useTranslation('common');
  const display = useFeatureConfig('dynamicData')?.display ?? 'builder';
  const data = useLexicalDynamicData();
  const editable = useLexicalEditable() && !!data && !data.readOnly;
  const chip = data?.chipFor(id, display) ?? {};
  const state = chip.label === undefined ? 'error' : (chip.state ?? 'ok');
  const answer = display === 'viewer' && state !== 'error';
  const Icon = chip.linked ? ExternalDynamicDataIcon : DynamicDataIcon;
  const iconColour = answer ? 'text-primary-100' : undefined;
  const canEdit = editable && !!data?.renderPicker && (data?.canEdit?.(id) ?? true);

  const remove = (event: MouseEvent) => {
    event.stopPropagation();
    editor.update(() => $getNodeByKey(nodeKey)?.remove());
  };

  return (
    <span
      contentEditable={false}
      title={chip.title}
      data-dynamic-data-chip={id}
      onMouseDown={keepCaret}
      onClick={canEdit ? () => editor.dispatchCommand(EDIT_DYNAMIC_DATA_COMMAND, nodeKey) : undefined}
      className={cx(
        'text-pbk-14 inline-flex max-w-full items-center rounded-2xl px-2 align-middle leading-[21px] font-normal whitespace-nowrap',
        answer ? ANSWER_COLOURS : cx(BACKGROUND[state], 'text-white'),
        canEdit ? 'cursor-pointer' : 'cursor-default',
      )}
    >
      <span className="flex w-5 shrink-0 items-center">
        <Icon className={cx('h-4 w-4', iconColour)} />
      </span>
      {/* Friendly names go up to 256 chars; the full name stays in `title` */}
      <span className="min-w-0 truncate">{chip.label ?? t('editor.dynamic-data.unknown')}</span>
      {editable && (
        <button
          type="button"
          aria-label={t('editor.dynamic-data.remove')}
          className="ml-1 flex shrink-0 cursor-pointer items-center"
          onClick={remove}
        >
          <X className={cx('h-4 w-4', iconColour)} aria-hidden />
        </button>
      )}
    </span>
  );
}
